import type {
  CheckboxOption,
  IssueField,
  IssueFieldType,
  IssueForm,
  RepositorySettings,
} from './types';

export const FIELD_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
export const SAFE_FILENAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*\.ya?ml$/i;

let fallbackUidCounter = 0;

export function createUid(prefix = 'uid'): string {
  const randomUuid = globalThis.crypto?.randomUUID?.bind(globalThis.crypto);
  if (randomUuid) return `${prefix}-${randomUuid()}`;

  fallbackUidCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${fallbackUidCounter.toString(36)}`;
}

/** Trim values, remove empty entries, and deduplicate case-insensitively. */
export function normalizeStringList(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const value of values) {
    const trimmed = value.trim();
    const key = trimmed.toLocaleLowerCase('en-US');
    if (!trimmed || seen.has(key)) continue;
    seen.add(key);
    normalized.push(trimmed);
  }

  return normalized;
}

function slugifyId(value: string): string {
  const slug = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/[-_]{2,}/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '')
    .toLocaleLowerCase('en-US');
  return slug || 'field';
}

export function generateUniqueId(preferred: string, existingIds: Iterable<string> = []): string {
  const existing = new Set(Array.from(existingIds, (id) => id.toLocaleLowerCase('en-US')));
  const base = slugifyId(preferred);
  if (!existing.has(base.toLocaleLowerCase('en-US'))) return base;

  let suffix = 2;
  while (existing.has(`${base}-${suffix}`.toLocaleLowerCase('en-US'))) suffix += 1;
  return `${base}-${suffix}`;
}

function createCheckboxOption(label = ''): CheckboxOption {
  return { uid: createUid('option'), label, required: false };
}

export function createField(type: IssueFieldType, existingIds: Iterable<string> = []): IssueField {
  if (type === 'markdown') {
    return {
      uid: createUid('field'),
      type,
      attributes: { value: '### Additional context' },
    };
  }

  const id = generateUniqueId(type === 'checkboxes' ? 'agreements' : type, existingIds);
  if (type === 'input') {
    return {
      uid: createUid('field'),
      type,
      id,
      attributes: { label: 'Input', description: '', placeholder: '', value: '' },
      validations: { required: false },
    };
  }

  if (type === 'textarea') {
    return {
      uid: createUid('field'),
      type,
      id,
      attributes: {
        label: 'Details',
        description: '',
        placeholder: '',
        value: '',
        render: '',
      },
      validations: { required: false },
    };
  }

  if (type === 'dropdown') {
    return {
      uid: createUid('field'),
      type,
      id,
      attributes: {
        label: 'Select an option',
        description: '',
        options: ['Option 1'],
        multiple: false,
      },
      validations: { required: false },
    };
  }

  return {
    uid: createUid('field'),
    type,
    id,
    attributes: {
      label: 'Acknowledgements',
      description: '',
      options: [createCheckboxOption('I agree')],
    },
  };
}

export function getFieldIds(form: Pick<IssueForm, 'body'>): string[] {
  return form.body.flatMap((field) => (field.type === 'markdown' ? [] : [field.id]));
}

export function duplicateField(field: IssueField, existingIds: Iterable<string>): IssueField {
  const copy = structuredClone(field);
  Object.defineProperty(copy, 'uid', {
    configurable: true,
    enumerable: true,
    value: createUid('field'),
  });
  if (copy.type !== 'markdown') copy.id = generateUniqueId(copy.id, existingIds);
  if (copy.type === 'checkboxes') {
    copy.attributes.options = copy.attributes.options.map((option) => ({
      ...option,
      uid: createUid('option'),
    }));
  }
  return copy;
}

export function sanitizeFilename(value: string, fallbackBase = 'issue-form'): string {
  const withoutPath = value.trim().replace(/\\/g, '/').split('/').pop() ?? '';
  const withoutExtension = withoutPath.replace(/\.ya?ml$/i, '');
  const safeBase = withoutExtension
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[.-]+|[.-]+$/g, '')
    .slice(0, 96);
  const fallback = fallbackBase.replace(/[^A-Za-z0-9_-]/g, '-') || 'issue-form';
  return `${safeBase || fallback}.yml`;
}

export function isSafeFilename(value: string): boolean {
  if (!SAFE_FILENAME_PATTERN.test(value) || value.length > 128 || value.includes('..')) {
    return false;
  }
  const base = value.replace(/\.ya?ml$/i, '').toLocaleLowerCase('en-US');
  return !new Set([
    'con',
    'prn',
    'aux',
    'nul',
    'com1',
    'com2',
    'com3',
    'com4',
    'com5',
    'com6',
    'com7',
    'com8',
    'com9',
    'lpt1',
    'lpt2',
    'lpt3',
    'lpt4',
    'lpt5',
    'lpt6',
    'lpt7',
    'lpt8',
    'lpt9',
  ]).has(base);
}

export function createEmptyForm(): IssueForm {
  return {
    uid: createUid('form'),
    filename: 'issue-form.yml',
    name: 'New issue form',
    description: 'Tell us about your issue.',
    title: '',
    labels: [],
    assignees: [],
    body: [createField('textarea')],
  };
}

export function createDefaultRepositorySettings(): RepositorySettings {
  return { blankIssuesEnabled: false, contactLinks: [] };
}

export function createContactLink(): RepositorySettings['contactLinks'][number] {
  return { uid: createUid('contact'), name: '', url: '', about: '' };
}
